import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';

const StatCard = ({ label, value, color, icon }) => (
  <div style={{
    background:'var(--surface)',
    border:'1px solid var(--border)',
    borderRadius:'12px',
    padding:'20px',
    position:'relative',
    overflow:'hidden',
    borderTop:`2px solid ${color}`,
  }}>
    <div style={{
      fontSize:'11px',
      fontFamily:'DM Mono,monospace',
      color:'var(--text3)',
      letterSpacing:'1px',
      textTransform:'uppercase',
      marginBottom:'10px',
    }}>
      {label}
    </div>
    <div style={{
      fontFamily:'Syne,sans-serif',
      fontSize:'36px',
      fontWeight:'800',
      color: color,
      lineHeight:1,
    }}>
      {value}
    </div>
    <div style={{
      position:'absolute',
      right:'16px', top:'16px',
      fontSize:'28px', opacity:0.1,
    }}>
      {icon}
    </div>
  </div>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] =
    useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, pendingRes] =
        await Promise.all([
          adminAPI.getStats(),
          adminAPI.getPendingVendors(),
        ]);
      setStats(statsRes.data.data.stats);
      setPending(pendingRes.data.data);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    setActionLoading(id + '_approve');
    try {
      await adminAPI.approveVendor(id);
      await loadData();
      alert('Vendor approved successfully!');
    } catch (err) {
      alert('Failed to approve vendor');
    } finally {
      setActionLoading('');
    }
  };

  const handleReject = async (id) => {
    const reason = prompt(
      'Enter rejection reason:'
    );
    if (!reason) return;
    setActionLoading(id + '_reject');
    try {
      await adminAPI.rejectVendor(id, { reason });
      await loadData();
      alert('Vendor rejected.');
    } catch (err) {
      alert('Failed to reject vendor');
    } finally {
      setActionLoading('');
    }
  };

  if (loading) return (
    <div style={{
      padding:'40px',
      color:'var(--text3)',
      fontFamily:'DM Mono,monospace',
    }}>
      Loading platform data...
    </div>
  );

  return (
    <div style={{ padding:'24px' }}>
      {/* Header */}
      <div style={{
        display:'flex',
        justifyContent:'space-between',
        alignItems:'center',
        marginBottom:'24px',
      }}>
        <div>
          <h1 style={{
            fontFamily:'Syne,sans-serif',
            fontSize:'22px', fontWeight:'800',
          }}>
            Command Center
          </h1>
          <p style={{
            color:'var(--text3)',
            fontSize:'13px', marginTop:'4px',
          }}>
            IRONWALL Service Bridge —
            Platform Overview
          </p>
        </div>
        <button
          onClick={() => navigate('/vendors')}
          style={{
            background:'var(--accent)',
            color:'var(--bg)',
            border:'none',
            padding:'10px 20px',
            borderRadius:'8px',
            fontSize:'13px',
            fontWeight:'600',
            cursor:'pointer',
          }}
        >
          Manage Vendors →
        </button>
      </div>

      {/* Stats Grid */}
      {stats && (
        <div style={{
          display:'grid',
          gridTemplateColumns:
            'repeat(4,1fr)',
          gap:'16px',
          marginBottom:'24px',
        }}>
          <StatCard
            label="Total Vendors"
            value={stats.total_vendors}
            color="var(--accent)"
            icon="◈"
          />
          <StatCard
            label="Approved Vendors"
            value={stats.approved_vendors}
            color="var(--green)"
            icon="✓"
          />
          <StatCard
            label="Pending Approval"
            value={stats.pending_vendors}
            color="var(--yellow)"
            icon="⏳"
          />
          <StatCard
            label="Total Engineers"
            value={stats.total_engineers}
            color="var(--purple)"
            icon="◉"
          />
          <StatCard
            label="Total Tickets"
            value={stats.total_tickets}
            color="var(--accent)"
            icon="◫"
          />
          <StatCard
            label="Active Tickets"
            value={stats.active_tickets}
            color="var(--orange)"
            icon="▷"
          />
          <StatCard
            label="Completed Tickets"
            value={stats.completed_tickets}
            color="var(--green)"
            icon="✓"
          />
          <StatCard
            label="New Vendors (Month)"
            value={stats.new_vendors_this_month}
            color="var(--purple)"
            icon="+"
          />
        </div>
      )}

      {/* Pending Vendors */}
      <div style={{
        background:'var(--surface)',
        border:'1px solid var(--border)',
        borderRadius:'12px',
        overflow:'hidden',
        marginBottom:'24px',
      }}>
        <div style={{
          padding:'16px 20px',
          borderBottom:'1px solid var(--border)',
          display:'flex',
          justifyContent:'space-between',
          alignItems:'center',
        }}>
          <div style={{
            fontFamily:'Syne,sans-serif',
            fontSize:'15px', fontWeight:'700',
          }}>
            Pending Vendor Approvals
          </div>
          <div style={{
            background:
              'rgba(255,192,64,0.15)',
            color:'var(--yellow)',
            padding:'3px 10px',
            borderRadius:'20px',
            fontSize:'12px',
            fontFamily:'DM Mono,monospace',
          }}>
            {pending.length} pending
          </div>
        </div>

        {pending.length === 0 ? (
          <div style={{
            padding:'40px',
            textAlign:'center',
            color:'var(--text3)',
            fontSize:'14px',
          }}>
            ✓ No pending approvals
          </div>
        ) : (
          <table style={{
            width:'100%',
            borderCollapse:'collapse',
          }}>
            <thead>
              <tr>
                {[
                  'Business','Owner',
                  'Category','City',
                  'Phone','Registered','Actions'
                ].map(h => (
                  <th key={h} style={{
                    padding:'10px 16px',
                    textAlign:'left',
                    fontSize:'10px',
                    fontFamily:
                      'DM Mono,monospace',
                    color:'var(--text3)',
                    letterSpacing:'1.5px',
                    textTransform:'uppercase',
                    borderBottom:
                      '1px solid var(--border)',
                    background:'var(--surface2)',
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pending.map(v => (
                <tr key={v.id}
                  style={{
                    borderBottom:
                      '1px solid var(--border)',
                  }}
                >
                  <td style={{
                    padding:'12px 16px',
                    fontWeight:'600',
                    fontSize:'13px',
                  }}>
                    {v.business_name}
                  </td>
                  <td style={{
                    padding:'12px 16px',
                    fontSize:'13px',
                    color:'var(--text2)',
                  }}>
                    {v.owner_name}
                  </td>
                  <td style={{
                    padding:'12px 16px',
                  }}>
                    <span style={{
                      background:
                        'rgba(0,212,255,0.1)',
                      color:'var(--accent)',
                      padding:'3px 10px',
                      borderRadius:'20px',
                      fontSize:'11px',
                      fontFamily:
                        'DM Mono,monospace',
                    }}>
                      {v.category_name
                        || 'General'}
                    </span>
                  </td>
                  <td style={{
                    padding:'12px 16px',
                    fontSize:'13px',
                    color:'var(--text2)',
                  }}>
                    {v.city}, {v.state}
                  </td>
                  <td style={{
                    padding:'12px 16px',
                    fontSize:'13px',
                    fontFamily:
                      'DM Mono,monospace',
                    color:'var(--text2)',
                  }}>
                    {v.phone}
                  </td>
                  <td style={{
                    padding:'12px 16px',
                    fontSize:'11px',
                    fontFamily:
                      'DM Mono,monospace',
                    color:'var(--text3)',
                  }}>
                    {new Date(v.created_at)
                      .toLocaleDateString('en-IN')}
                  </td>
                  <td style={{
                    padding:'12px 16px',
                  }}>
                    <div style={{
                      display:'flex',
                      gap:'8px',
                    }}>
                      <button
                        onClick={() =>
                          handleApprove(v.id)}
                        disabled={
                          actionLoading ===
                          v.id+'_approve'
                        }
                        style={{
                          background:
                            'rgba(0,229,160,0.15)',
                          color:'var(--green)',
                          border:
                            '1px solid rgba(0,229,160,0.3)',
                          padding:'5px 14px',
                          borderRadius:'6px',
                          fontSize:'12px',
                          fontWeight:'600',
                          cursor:'pointer',
                        }}
                      >
                        {actionLoading ===
                          v.id+'_approve'
                          ? '...' : '✓ Approve'}
                      </button>
                      <button
                        onClick={() =>
                          handleReject(v.id)}
                        disabled={
                          actionLoading ===
                          v.id+'_reject'
                        }
                        style={{
                          background:
                            'rgba(255,77,109,0.15)',
                          color:'var(--red)',
                          border:
                            '1px solid rgba(255,77,109,0.3)',
                          padding:'5px 14px',
                          borderRadius:'6px',
                          fontSize:'12px',
                          fontWeight:'600',
                          cursor:'pointer',
                        }}
                      >
                        ✕ Reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;